import { useEffect, useRef } from "react";
import { ArtworkImage } from "@/components/ArtworkImage";
import { ARTWORK } from "@/config/artwork";

export const SpaceBackground = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let stars = [];
    let frame = 0;
    let w = 0;
    let h = 0;

    const draw = (loop) => {
      ctx.clearRect(0, 0, w, h);
      for (const s of stars) {
        if (loop) {
          s.a += s.s;
          s.y += s.vy;
          if (s.y > h) s.y = -2;
        }
        const alpha = 0.25 + Math.abs(Math.sin(s.a)) * 0.75;
        ctx.beginPath();
        ctx.fillStyle = `rgba(${s.hue},${alpha})`;
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
      if (loop) frame = requestAnimationFrame(() => draw(true));
    };

    const build = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.min(320, Math.floor((w * h) / 6500));
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 1.3 + 0.2,
        a: Math.random(),
        s: Math.random() * 0.015 + 0.004,
        hue: Math.random() > 0.85 ? "0,255,102" : Math.random() > 0.5 ? "0,240,255" : "255,255,255",
        vy: Math.random() * 0.04 + 0.01,
      }));
      if (reduced) draw(false);
    };

    const start = () => {
      if (!reduced && !frame) frame = requestAnimationFrame(() => draw(true));
    };
    const stop = () => {
      cancelAnimationFrame(frame);
      frame = 0;
    };
    const onVisibility = () => (document.hidden ? stop() : start());

    build();
    start();
    window.addEventListener("resize", build);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      window.removeEventListener("resize", build);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <div className="space-bg" aria-hidden="true" data-testid="site-wallpaper-background">
      <picture className="site-wallpaper-picture" data-testid="site-wallpaper-picture">
        <source
          media="(max-width: 639px) and (orientation: portrait)"
          srcSet={ARTWORK.wallpaperMobile.srcSet}
          sizes="100vw"
          width={ARTWORK.wallpaperMobile.width}
          height={ARTWORK.wallpaperMobile.height}
          data-testid="site-wallpaper-mobile-source"
        />
        <ArtworkImage artwork={ARTWORK.space} sizes="100vw" priority className="site-wallpaper-image" testId="site-wallpaper-image" />
      </picture>
      <canvas ref={ref} />
    </div>
  );
};
